import { useCallback, useEffect, useRef, useState } from 'react';
import { analyticsService } from '../services/AnalyticsService';
import { sessionReportingService } from '../services/SessionReportingService';
import { verificationLifecycleService } from '../services/VerificationLifecycleService';
import { logger } from '../utils/logger';
import { useScreenNavigation } from './useScreenNavigation';
import { clearPendingEngineResult } from '../utils/pendingEngineResult';

const reportingLogger = logger.clone({ labels: { component: 'SessionReporting' }});

/** While the result is unconfirmed, Portal is re-read quietly at this interval. */
export const UNCONFIRMED_RECHECK_MS = 15_000;

type CompletionStatus = 'verified' | 'failed' | 'cancelled' | null;

/**
 * `reporting`: Portal has not yet established the outcome (first attempt or
 * bounded retries). `confirmed`: Portal confirmed VERIFIED. `unconfirmed`:
 * the retry budget ended without an established outcome; neither success nor
 * failure may be presented until an explicit retry settles it.
 */
type ReportingPhase = 'reporting' | 'confirmed' | 'unconfirmed';

interface UseSessionReportingOptions {
  sessionId?: string;
  skipCompletionReporting: boolean;
  reportingSkipReason?: string;
}

export function useSessionReporting({
  sessionId,
  skipCompletionReporting,
  reportingSkipReason,
}: UseSessionReportingOptions) {
  const { navigateToScreen } = useScreenNavigation();
  const [phase, setPhase] = useState<ReportingPhase>('reporting');
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus>(null);
  const hasTrackedFlowCompletedRef = useRef(false);

  useEffect(() => {
    if (phase !== 'reporting') {
      return;
    }

    let mounted = true;

    if (!hasTrackedFlowCompletedRef.current) {
      hasTrackedFlowCompletedRef.current = true;
      analyticsService.trackFlowCompleted(true);
    }

    const reportSession = async () => {
      if (skipCompletionReporting) {
        reportingLogger.info('Skipping session reporting for pre-completed callback', {
          labels: {
            reason: reportingSkipReason || 'unknown',
            sessionId: sessionId || 'unknown',
          },
        });
        if (mounted) {
          setCompletionStatus('verified');
          setPhase('confirmed');
        }
        return;
      }

      if (!sessionId) {
        reportingLogger.warn('No sessionId available for reporting');
        if (mounted) setPhase('unconfirmed');
        return;
      }

      // The service owns retries and a single in-flight report per session,
      // so StrictMode re-runs and consecutive screens join one request.
      const outcome = await sessionReportingService.reportSessionCompletion({
        sessionId,
        status: 'verified',
        verificationData: {
          method: 'facial',
        },
      });
      if (!mounted) return;

      if (outcome.kind !== 'unconfirmed') clearPendingEngineResult();
      switch (outcome.kind) {
        case 'confirmed':
          setCompletionStatus('verified');
          setPhase('confirmed');
          return;
        case 'unconfirmed':
          reportingLogger.warn('Session completion is not yet confirmed by Portal');
          setPhase('unconfirmed');
          return;
        case 'reconciled':
          navigateToScreen(verificationLifecycleService.screenFor(outcome.lifecycle));
          return;
        case 'expired':
        case 'rejected':
          // The recovery screen re-reads Portal and renders expiry, recovery
          // or return-to-site from authoritative state.
          navigateToScreen('failure');
          return;
      }
    };

    void reportSession();

    return () => {
      mounted = false;
    };
  }, [
    navigateToScreen,
    phase,
    reportingSkipReason,
    sessionId,
    skipCompletionReporting,
  ]);

  // While unconfirmed, re-read Portal quietly so a result recorded late
  // moves the customer on without a tap. If Portal can no longer record it,
  // the recovery screen offers the next step.
  useEffect(() => {
    if (phase !== 'unconfirmed' || !sessionId) return;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const lifecycle = await verificationLifecycleService.reconcile();
        if (stopped || lifecycle.sessionId !== sessionId) return;
        if (lifecycle.sessionStatus === 'VERIFIED') {
          clearPendingEngineResult();
          setCompletionStatus('verified');
          setPhase('confirmed');
          return;
        }
        const screen = verificationLifecycleService.screenFor(lifecycle);
        if (screen !== 'success') {
          clearPendingEngineResult();
          navigateToScreen(screen);
        }
      } catch {
        // A failed read leaves the choices on screen; the next tick tries again.
      }
    }, UNCONFIRMED_RECHECK_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [navigateToScreen, phase, sessionId]);

  const retryReporting = useCallback(() => {
    setPhase((current) => (current === 'unconfirmed' ? 'reporting' : current));
  }, []);

  return {
    isReportingComplete: phase === 'confirmed',
    isConfirmationPending: phase === 'reporting',
    isConfirmationUnavailable: phase === 'unconfirmed',
    completionStatus,
    retryReporting,
  };
}
